/**
 * Every item at once (M2 review page, like the figure line-up): the 47 foods, the 35 power-ups, or
 * the rest (the monsters' shots, the giant bonus items, the boss) in a numbered grid on level 1's
 * stage, drawn as the game draws them. The number under each is its table index.
 *
 *   ?scene=items&set=foods      FOODS 0-46
 *   ?scene=items&set=powerups   POWERUPS 0-34
 *   ?scene=items&set=misc       fireball, bottle, rock, laser; giant cupcake, melon, diamonds; the boss
 *   &t=0.3                      freeze the turn at t seconds (captures)
 */

import { Mesh, Scene, Vector3 } from 'three';
import stageGrade from '../../content/grades/stage.json';
import { level } from '../levels';
import { gridFromRows } from '../levels/grid';
import { FOODS } from '../sim/bubbles/food';
import { POWERUPS } from '../sim/items/powerups';
import type { ProjectileKind } from '../sim/types';
import { buildBoss } from '../render/actors/boss';
import { foodModel } from '../render/actors/food';
import { powerupModel } from '../render/actors/powerups';
import { projectileModel } from '../render/actors/projectiles';
import type { MeshBuilder } from '../render/kit/MeshBuilder';
import { StageCamera } from '../render/camera/StageCamera';
import { levelLook } from '../render/levelLook';
import { createGradientMap, createToonMaterial, OUTLINE } from '../render/materials/toon';
import { type Grade, Palette } from '../render/palette';
import { RenderPipeline } from '../render/pipeline/RenderPipeline';
import { createStageLights, StageView } from '../render/stage/StageView';
import { drawText, textWidth } from '../ui/font8';
import { createHudCanvas } from '../ui/hud/panels';
import { chooseSize, createRenderer, exposeLook, num } from './devKit';

const PROJECTILES: readonly ProjectileKind[] = ['fireball', 'bottle', 'rock', 'laser'];
/** The giant bonus items, as drawn at a clear (02 §7.6): the food each staff or box drops. */
const GIANTS = ['cupcake', 'watermelon', 'yellow diamond', 'blue diamond', 'purple diamond'] as const;

interface Entry {
  label: string;
  build: () => MeshBuilder | null;
  scale?: number;
}

function entries(set: string, p: Palette): Entry[] {
  if (set === 'powerups') return POWERUPS.map((u, i) => ({ label: `${i}`, build: () => powerupModel(u.name, p) }));
  if (set === 'misc') {
    return [
      ...PROJECTILES.map((k) => ({ label: k.slice(0, 4).toUpperCase(), build: () => projectileModel(k, p) })),
      ...GIANTS.map((g) => ({ label: 'GIANT', build: () => foodModel(g, p), scale: 2.6 })),
    ];
  }
  return FOODS.map((f, i) => ({ label: `${i}`, build: () => foodModel(f.name, p) }));
}

export function startItemsLineup(canvas: HTMLCanvasElement, params: URLSearchParams): void {
  const def = level(1);
  const look = levelLook(def);
  const palette = new Palette(look);
  const gradientMap = createGradientMap();
  const renderer = createRenderer(canvas);
  const grid = gridFromRows(def.tiles);
  const scene = new Scene();
  scene.add(new StageView({ grid, style: look.style, palette, gradientMap, seed: 1 }).group);
  scene.add(createStageLights(stageGrade as Grade, grid.width, grid.height));
  const material = createToonMaterial({ gradientMap, outline: OUTLINE.character, depthBias: 1.5 });

  const set = params.get('set') ?? 'foods';
  const list = entries(set, palette);
  const cols = set === 'misc' ? 5 : 8;
  const dx = set === 'misc' ? 5.6 : 3.6;
  const dy = set === 'misc' ? 7 : 3.7;
  const meshes: { mesh: Mesh; at: Vector3; label: string }[] = [];
  list.forEach((e, i) => {
    const mb = e.build();
    if (!mb) return;
    const mesh = new Mesh(mb.build(), material);
    const at = new Vector3((i % cols - (cols - 1) / 2) * dx, 9.5 - Math.floor(i / cols) * dy, 0);
    mesh.position.copy(at);
    mesh.scale.setScalar(1.35 * (e.scale ?? 1));
    scene.add(mesh);
    meshes.push({ mesh, at, label: e.label });
  });
  if (set === 'misc') {
    const boss = buildBoss(palette, material);
    boss.root.position.set(0, -12.3, 0);
    boss.face(-1);
    scene.add(boss.root);
  }

  const size = chooseSize(params);
  const camera = new StageCamera(size, num(params, 'ppt', 14) * size.scale, num(params, 'pitch', 18));
  const pipeline = new RenderPipeline(renderer, palette.buildLut(), palette.color('ink.0'), size);
  pipeline.setGrade(stageGrade as Grade, look.fog);
  const hud = createHudCanvas(document.body);
  const ctx = hud.canvas.getContext('2d');
  const frozen = params.has('t');
  const start = performance.now();
  const frame = (): void => {
    const t = frozen ? num(params, 't', 0) : (performance.now() - start) / 1000;
    for (const m of meshes) m.mesh.rotation.y = Math.sin(t * 2) * 0.5;
    pipeline.render(scene, camera);
    hud.place(pipeline.viewRect);
    if (ctx) {
      ctx.clearRect(0, 0, hud.canvas.width, hud.canvas.height);
      for (const m of meshes) {
        const s = camera.worldToScreen(m.at);
        const x = Math.round(s.x / size.scale - textWidth(m.label) / 2);
        drawText(ctx, m.label, x, Math.round(s.y / size.scale) + 4, '#ffffff');
      }
    }
    if (!frozen) requestAnimationFrame(frame);
  };
  frame();
  exposeLook(pipeline, frame, { camera }, hud.canvas);
}
